import {
  FormDefinitionApiResponse,
  GetFormDefinitionParams,
  GetOwnerFormsParams,
  OwnerFormsApiResponse,
  SubmitBookingAddOnsDto,
} from 'store/booking-add-ons/types';
import { AjaxResponse, ajax } from 'rxjs/ajax';
import {
  BookingsParams,
  CancelBookingParams,
  CreateBookingParams,
  GetResourceOptions,
  OwnerParams,
} from 'shared/types';
import {
  Complete3DSCartApiResponse,
  CompleteCartDto,
  CreateCartAPIResponse,
  CreateCartDto,
  GetCartApiResponse,
  SubmitCartApiResponse,
  SubmitOrderDto,
} from 'store/cart/types';
import { BookingStatus, CreateBookingAPIResponse, GetBookingsAPIResponse } from 'store/bookings/types';
import { CurrentUserResponse, GetCurrentUserPayload } from 'store/user/types';
import { FilterParams, ResourcesAPIResponse } from 'store/resources/types';
import { ResourceCollectionsAPIResponse } from 'store/collections/types';
import {
  FilterSpacesQueryParams,
  FloorsAPIResponse,
  GetFloorsQueryParams,
  SpaceAPIResponse,
} from 'store/spaceManager/types';
import {
  FloorPlanAppInstanceConfigResponse,
  GetFloorPlanAppInstanceConfigParams,
} from 'store/floorplan-app-instance-config/types';
import {
  AppInstanceConfigWithLocations,
  GetAppInstanceConfigsAPIResponse,
  GetAppInstanceConfigsParams,
  OnboardUseParams,
} from '../app-instance-configs/types';
import { GetPriceAPIResponse, GetPriceParams } from 'store/price/types';
import {
  GetResourceAvailableDatesAPIResponse,
  GetResourceAvailableDatesParams,
} from '../resource-available-dates/types';
import { GetResourceTimeRangesAPIResponse, GetResourceTimeRangesParams } from '../resource-time-ranges/types';
import {
  GetTransactionResponse,
  GetTransactionsAPIResponse,
  PatchTransactionDto,
  RedirectTransactionResponse,
  UpdateCheckInAPIResponse,
} from 'store/transactions/types';
import QueryString, { stringify } from 'qs';
import { SavePaymentMethodResponse, SavedPaymentMethod, UpdatePaymentMethodRequest } from 'store/payment/types';

import { BuildingResponse } from 'store/building/types';
import { BuildingTheme } from 'store/theme/types';
import { BOOKINGS_PAGE_SIZE, BOOKING_STATUSES, CHECK_IN_STATUSES } from 'shared/consts';
import { Observable } from 'rxjs';
import { RESOURCE_BOOKING } from 'shared/consts/vertical-name';
import { ResourceAPIResponse } from 'store/resource/types';
import { ResourceFavoriteAPIResponse, ToggleResourceFavoriteParams } from 'store/resource-favorite/types';
import { SavePaymentMethodRequest } from '../payment/types';
import { UiMetadata } from 'store/ui-metadata/types';

/**
 * RES-1174. The status set the tenant transactions read asks for, and four things about it that
 * are not obvious from the line that uses it.
 *
 * 1. It REPLACES rather than extends. helix applies its own default (Pending, Accepted,
 *    Pending_Payment) only when the request supplies no `statuses`, so naming the two rejections
 *    without re-naming those three would drop every confirmed and pending booking from the list.
 *    A failed 3DS payment (RES-1407) is asked for too, so My Bookings can offer it a retry.
 *
 * 2. It belongs on the request, not on a call site. The transactions slice is shared, the epic
 *    replaces it wholesale on every success, and thirteen places dispatch `getTransactions.request`
 *    -- several while the tenant is on My Bookings (closing a receipt, cancelling a booking). Set
 *    it at one dispatcher and the first bare refetch silently narrows the list back; worse,
 *    `useUpcomingBookings` only dispatches when the store is empty, so on the normal navigation
 *    path (landing page fetches first) it would never send the widened request at all.
 *
 * 3. A pre-joined STRING, never an array. helix splits this parameter by running a regex against
 *    the raw value; `stringify` here carries no `arrayFormat`, so qs's default would serialise an
 *    array as `statuses[0]=...`, which arrives as an array and throws inside that splitter.
 *
 * 4. It cannot be built from one enum. `Pending_Payment` is absent from `BookingStatus` and lives
 *    only on `BOOKING_STATUSES`; the rejection values live only on `BookingStatus`.
 *
 * Values are case-sensitive and validated server-side -- a wrong one is a 422 that empties the
 * whole list rather than a dropped value.
 */
const TRANSACTION_STATUSES = [
  BOOKING_STATUSES.PENDING,
  BOOKING_STATUSES.ACCEPTED,
  BOOKING_STATUSES.PAYMENT_PENDING,
  BookingStatus.DENIED_AVAILABLE,
  BookingStatus.DENIED_UNAVAILABLE,
  BookingStatus.PAYMENT_CHALLENGE_CANCELED,
].join(',');

interface TransactionsPageParams {
  offset?: number;
  limit?: number;
}

interface BookingsTabQuery {
  isUpcoming: boolean;
  orderBy: string;
  order: string;
}

/** The only thing that differs between the two tab reads. Upcoming runs forward from the soonest
 * start; Past runs backward from the most recent end. */
const BOOKINGS_TAB_QUERY = {
  upcoming: { isUpcoming: true, orderBy: 'start_at', order: 'asc' },
  past: { isUpcoming: false, orderBy: 'end_at', order: 'desc' },
} as const satisfies Record<string, BookingsTabQuery>;

export class ApiClient {
  constructor(
    private baseUrl: string,
    private authToken?: string | null,
    private buildingUuid?: string | null,
    private appBrand?: string | null,
    private appInstanceConfigUuid?: string | null,
    private locationId?: string | null,
    private locale?: string | null,
  ) {}

  private get DefaultHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      Authorization: this.authToken,
      'Hqo-Building-UUID': this.buildingUuid,
      'Hqo-App-Brand': this.appBrand,
    };
  }

  private get DefaultParams(): Record<string, string> {
    return {
      appInstanceConfigUuid: this.appInstanceConfigUuid,
      locationId: this.locationId,
      lang: this.locale,
    };
  }

  private get DefaultParamsOptions(): QueryString.IStringifyOptions {
    return {
      skipNulls: true,
    };
  }

  getCurrentUser({
    authToken,
    appBrand,
    buildingUuid,
  }: GetCurrentUserPayload): Observable<AjaxResponse<CurrentUserResponse>> {
    return ajax.get(`${this.baseUrl}/api/users/current?${stringify(this.DefaultParams, this.DefaultParamsOptions)}`, {
      'Content-Type': 'application/json',
      Authorization: authToken ?? this.authToken,
      'Hqo-Building-UUID': buildingUuid ?? this.buildingUuid,
      'Hqo-App-Brand': appBrand ?? this.appBrand,
    });
  }

  getResource(
    ownerType: string,
    _ownerUuid: string,
    resourceUuid: string,
    getResourceOptions?: GetResourceOptions,
  ): Observable<AjaxResponse<ResourceAPIResponse>> {
    const { closestAvailability, include, start_at } = getResourceOptions || {};
    const requestParams = { ...this.DefaultParams, closestAvailability, include, start_at };

    return ajax.get(
      `${this.baseUrl}/api/resource-booking/v2/${ownerType}/${this.buildingUuid}/resources/${resourceUuid}?${stringify(
        requestParams,
        this.DefaultParamsOptions,
      )}`,
      this.DefaultHeaders,
    );
  }

  getResources(
    ownerType: string,
    _ownerUuid: string,
    filterParams: FilterParams,
    appInstanceConfigs?: Array<AppInstanceConfigWithLocations>,
  ): Observable<AjaxResponse<ResourcesAPIResponse>> {
    const requestParams = {
      ...this.DefaultParams,
      ...filterParams,
      ...(appInstanceConfigs?.length > 1 ? { appInstanceConfigs } : {}),
    };
    const paramString = stringify(requestParams, this.DefaultParamsOptions);

    return ajax.get(
      `${this.baseUrl}/api/resource-booking/v2/${ownerType}/${this.buildingUuid}/resources?${paramString}`,
      this.DefaultHeaders,
    );
  }

  addResourceFavorite({
    ownerType,
    resourceUuid,
  }: Pick<ToggleResourceFavoriteParams, 'ownerType' | 'resourceUuid'>): Observable<
    AjaxResponse<ResourceFavoriteAPIResponse>
  > {
    return ajax.post(
      `${this.baseUrl}/api/resource-booking/v2/${ownerType}/${this.buildingUuid}/resources/${resourceUuid}/favorite`,
      {},
      this.DefaultHeaders,
    );
  }

  removeResourceFavorite({
    ownerType,
    resourceUuid,
  }: Pick<ToggleResourceFavoriteParams, 'ownerType' | 'resourceUuid'>): Observable<AjaxResponse<void>> {
    return ajax.delete(
      `${this.baseUrl}/api/resource-booking/v2/${ownerType}/${this.buildingUuid}/resources/${resourceUuid}/favorite`,
      this.DefaultHeaders,
    );
  }

  getResourceCollections(
    buildingUuid: string,
    companyUuid?: string,
    lang?: string,
    include?: Array<'resources' | 'types' | 'translations'>,
  ): Observable<AjaxResponse<ResourceCollectionsAPIResponse>> {
    const requestParams: Record<string, string> = {
      ...this.DefaultParams,
    };

    if (companyUuid) {
      requestParams.company_uuid = companyUuid;
    }

    if (buildingUuid) {
      requestParams.building_uuid = buildingUuid;
    }

    if (lang) {
      requestParams.lang = lang;
    }

    if (include && include.length > 0) {
      requestParams.include = include.join(',');
    }

    const paramString = stringify(requestParams, this.DefaultParamsOptions);

    return ajax.get(
      `${this.baseUrl}/api/resource-booking/v2/building/${buildingUuid}/resource-collections?${paramString}`,
      this.DefaultHeaders,
    );
  }

  createCart(createCartDto: CreateCartDto, lang?: string | null): Observable<AjaxResponse<CreateCartAPIResponse>> {
    const { locationId, appInstanceConfigUuid, ...rest } = createCartDto;
    const baseParams = lang ? { ...this.DefaultParams, lang } : this.DefaultParams;
    const withLocation = locationId ? { ...baseParams, locationId } : baseParams;
    const defaultParams = appInstanceConfigUuid ? { ...withLocation, appInstanceConfigUuid } : withLocation;

    return ajax.post(
      `${this.baseUrl}/api/payments/v1/carts?${stringify(defaultParams, this.DefaultParamsOptions)}`,
      rest,
      this.DefaultHeaders,
    );
  }

  /*
   * RES-1050 — hand a post-booking add-on selection to the Service Request path.
   *
   * Path shape mirrors the existing booking routes (`PATCH`/`DELETE` on
   * `.../resources/:resourceId/bookings/:bookingId`), where `:bookingId` resolves to
   * `resource_booking_details.id`. ⛔ The endpoint is built by RES-1051; this is the client half.
   */
  /*
   * ⛔ THE BODY IS ENUMERATED TWICE — in this destructure and in the object literal below — and a
   * field missing from EITHER is dropped here, client-side, before helix's `whitelist: true` ever
   * sees it. RES-1057's `form_response` was added to `SubmitBookingAddOnsDto` as well; adding it
   * there alone leaves this method compiling and posting a body without it, so a correct encoder and
   * a green test suite still put nothing on the wire. Assert on the body handed to this method, not
   * on the dispatched action.
   */
  submitBookingAddOns({
    ownerType,
    ownerUuid,
    resourceId,
    bookingId,
    add_ons,
    form_response,
  }: SubmitBookingAddOnsDto): Observable<AjaxResponse<unknown>> {
    return ajax.post(
      `${
        this.baseUrl
      }/api/resource-booking/v2/${ownerType}/${ownerUuid}/resources/${resourceId}/bookings/${bookingId}/add-ons?${stringify(
        this.DefaultParams,
        this.DefaultParamsOptions,
      )}`,
      { add_ons, form_response },
      this.DefaultHeaders,
    );
  }

  /*
   * ⛔ RES-1057. NO `/api/` PREFIX ON THESE TWO, AND THAT IS NOT AN OVERSIGHT.
   *
   * Every other method on this class is `${baseUrl}/api/<service>/…`, because `/api` is HELIX's own
   * global prefix (`app.setGlobalPrefix('api')`), not a gateway convention. work-orders and forms sit
   * beside helix on the same origin WITHOUT it. Measured against preprod, unauthenticated:
   *
   *     POST /work-orders/v1/…/owner-forms          -> 403   (route exists, refused)
   *     POST /api/work-orders/v1/…/owner-forms      -> 404   (no such route)
   *     GET  /forms/v1/forms/:uuid/definition       -> 403
   *     GET  /api/forms/v1/forms/:uuid/definition   -> 404
   *     POST /api/resource-booking/v2/… (control)   -> 401   (helix route, exists)
   *
   * So copying the house pattern here yields a 404 — and because both reads fail open by design, the
   * form would simply never appear, with nothing logged and nothing to debug.
   */

  getOwnerForms({ buildingUuid, items }: GetOwnerFormsParams): Observable<AjaxResponse<OwnerFormsApiResponse>> {
    return ajax.post(
      `${this.baseUrl}/work-orders/v1/tenants/current/buildings/${buildingUuid}/owner-forms`,
      { items },
      this.DefaultHeaders,
    );
  }

  /*
   * ⛔ `app=tenant` IS LOAD-BEARING AND UNVALIDATED. forms-service extracts it as a raw `@Query('app')`
   * with no pipe, so a typo is indistinguishable from omitting it. Without it, `resolveEditorRoles`
   * falls through to an admin lookup keyed on the template's BUILDING — and a vendor's template is
   * company-scoped, so it has none — and every field that declares an editable role comes back
   * `canEdit: false`. The form then renders, looks right, and cannot be typed into.
   */
  getFormDefinition({
    templateUuid,
    lang,
  }: GetFormDefinitionParams): Observable<AjaxResponse<FormDefinitionApiResponse>> {
    return ajax.get(
      `${this.baseUrl}/forms/v1/forms/${templateUuid}/definition?${stringify(
        { app: 'tenant', lang },
        this.DefaultParamsOptions,
      )}`,
      this.DefaultHeaders,
    );
  }

  getBookings({
    ownerType,
    ownerUuid,
    resourceUuid,
    permission,
    ...queryParams
  }: BookingsParams): Observable<AjaxResponse<GetBookingsAPIResponse>> {
    const params = {
      ...this.DefaultParams,
      start_time: queryParams.start_date,
      end_time: queryParams.end_date,
      limit: queryParams.limit,
      offset: queryParams.offset,
    };

    return ajax.get(
      `${
        this.baseUrl
      }/api/resource-booking/v2/${ownerType}/${ownerUuid}/resources/${resourceUuid}/bookings/${permission}?${stringify(
        params,
        this.DefaultParamsOptions,
      )}`,
      this.DefaultHeaders,
    );
  }

  /**
   * One page of a bookings tab. `offset`/`limit` are the server's: helix pages by transaction, so a
   * booking is never split across pages, and `total` comes back beside `data.transactions` so the
   * caller can tell whether another page exists. Every list order ends on `id` server-side, which is
   * what makes offset paging stable across pages.
   */
  private bookingsPageUrl(tab: BookingsTabQuery, offset: number, limit: number): string {
    return `${this.baseUrl}/api/tenant/v1/users/current/transactions?types=resources_booking&is_upcoming=${
      tab.isUpcoming
    }&order_by=${tab.orderBy}&order=${
      tab.order
    }&statuses=${TRANSACTION_STATUSES}&offset=${offset}&limit=${limit}&${stringify(
      this.DefaultParams,
      this.DefaultParamsOptions,
    )}`;
  }

  /** An empty argument is the first page, which callers passing `{}` to mean "refresh" rely on. */
  getTransactions({ offset = 0, limit = BOOKINGS_PAGE_SIZE }: TransactionsPageParams = {}): Observable<
    AjaxResponse<GetTransactionsAPIResponse>
  > {
    return ajax.get(this.bookingsPageUrl(BOOKINGS_TAB_QUERY.upcoming, offset, limit), this.DefaultHeaders);
  }

  /**
   * A separate request rather than a flag: the two lists sit side by side in the store and neither
   * may overwrite the other. `is_upcoming=false` is the exact complement of the Upcoming read over
   * the same `statuses` (helix #11185, getWhereOptionsForTab) — dropping `statuses` here would make
   * Past keep the bookings Upcoming already shows.
   */
  getPastTransactions({ offset = 0, limit = BOOKINGS_PAGE_SIZE }: TransactionsPageParams = {}): Observable<
    AjaxResponse<GetTransactionsAPIResponse>
  > {
    return ajax.get(this.bookingsPageUrl(BOOKINGS_TAB_QUERY.past, offset, limit), this.DefaultHeaders);
  }

  patchTransaction(patchTransactionDto: PatchTransactionDto) {
    const { transactionId, ...body } = patchTransactionDto;

    return ajax.patch(`${this.baseUrl}/api/transactions/${transactionId}`, body, this.DefaultHeaders);
  }

  checkIn(transactionUuid: string): Observable<AjaxResponse<UpdateCheckInAPIResponse>> {
    const body = { status: CHECK_IN_STATUSES.CONFIRMED };

    return ajax.patch(`${this.baseUrl}/api/check-in/${transactionUuid}`, body, this.DefaultHeaders);
  }

  getPrice({
    ownerType,
    resourceId,
    lang,
    ...queryParams
  }: GetPriceParams): Observable<AjaxResponse<GetPriceAPIResponse>> {
    const params = {
      ...this.DefaultParams,
      from: queryParams.from,
      to: queryParams.to,
      lang,
    };

    return ajax.get(
      `${this.baseUrl}/api/resource-booking/v2/${ownerType}/${
        this.buildingUuid
      }/resources/${resourceId}/price?${stringify(params, this.DefaultParamsOptions)}`,
      this.DefaultHeaders,
    );
  }

  getResourceAvailableDates({
    ownerType,
    resourceId,
    from,
    to,
  }: GetResourceAvailableDatesParams): Observable<AjaxResponse<GetResourceAvailableDatesAPIResponse>> {
    const params = stringify({ from: from.replace(/\./g, ''), to: to.replace(/\./g, ''), ...this.DefaultParams });

    return ajax.get(
      `${this.baseUrl}/api/resource-booking/v2/${ownerType}/${this.buildingUuid}/resources/${resourceId}/available-dates?${params}`,
      this.DefaultHeaders,
    );
  }

  getAppInstanceConfigs({
    owners,
    lang,
    appInstanceConfigUuid,
  }: GetAppInstanceConfigsParams): Observable<AjaxResponse<GetAppInstanceConfigsAPIResponse>> {
    const params = stringify(appInstanceConfigUuid ? { owners, appInstanceConfigUuid, lang } : { owners, lang });

    return ajax.get(`${this.baseUrl}/api/resource-booking/v2/app-instance-configs?${params}`, this.DefaultHeaders);
  }

  getFloorPlanAppInstanceConfig({
    building_uuid,
  }: GetFloorPlanAppInstanceConfigParams): Observable<AjaxResponse<FloorPlanAppInstanceConfigResponse>> {
    return ajax.get(
      `${this.baseUrl}/api/floorplan/v1/BUILDING/${building_uuid}/app-instance-config`,
      this.DefaultHeaders,
    );
  }

  onboardUser({ appInstanceConfigUuid }: OnboardUseParams) {
    return ajax.post(
      `${this.baseUrl}/api/resource-booking/v2/app-instance-configs/${appInstanceConfigUuid}/onboard-user`,
      { accept_terms_and_conditions: true },
      this.DefaultHeaders,
    );
  }

  createBooking({
    ownerType,
    ownerUuid,
    resourceUuid,
    booking,
  }: CreateBookingParams): Observable<AjaxResponse<CreateBookingAPIResponse>> {
    const url = `${
      this.baseUrl
    }/api/resource-booking/v2/${ownerType}/${ownerUuid}/resources/${resourceUuid}/bookings?${stringify(
      this.DefaultParams,
      this.DefaultParamsOptions,
    )}`;
    const body = {
      form_responses: booking.form_responses,
      contacted: booking.contacted,
      description: booking.description,
      name: booking.name,
      start_at: booking.startAt,
      end_at: booking.endAt,
    };

    return ajax.post(url, { booking: body }, this.DefaultHeaders);
  }

  cancelBooking({
    ownerType,
    ownerUuid,
    resourceUuid,
    bookingUuid,
  }: CancelBookingParams): Observable<AjaxResponse<CancelBookingParams>> {
    const url = `${
      this.baseUrl
    }/api/resource-booking/v2/${ownerType}/${ownerUuid}/resources/${resourceUuid}/bookings/${bookingUuid}/mine?${stringify(
      this.DefaultParams,
      this.DefaultParamsOptions,
    )}`;

    return ajax.delete(url, this.DefaultHeaders);
  }

  getBuildingTheme(buildingUuid: string): Observable<AjaxResponse<BuildingTheme>> {
    return ajax.get(
      `${this.baseUrl}/api/buildings/${buildingUuid}/theme?${stringify(this.DefaultParams, this.DefaultParamsOptions)}`,
      this.DefaultHeaders,
    );
  }

  getBuilding(buildingUuid: string): Observable<AjaxResponse<BuildingResponse>> {
    return ajax.get(
      `${this.baseUrl}/api/tenant/v1/buildings/${buildingUuid}?${stringify(
        this.DefaultParams,
        this.DefaultParamsOptions,
      )}`,
      this.DefaultHeaders,
    );
  }

  getResourceTimeRanges({
    ownerType,
    resourceId,
    ...restParams
  }: GetResourceTimeRangesParams): Observable<AjaxResponse<GetResourceTimeRangesAPIResponse>> {
    let params;
    if ('startDates' in restParams) {
      const { startDates, ...rest } = restParams;
      params = stringify({ dates: startDates, ...rest, ...this.DefaultParams });
    } else {
      params = stringify({ ...restParams, ...this.DefaultParams });
    }

    return ajax.get(
      `${this.baseUrl}/api/resource-booking/v2/${ownerType}/${this.buildingUuid}/resources/${resourceId}/time-ranges?${params}`,
      this.DefaultHeaders,
    );
  }

  getFloors({ buildingUuid, floors_sorting, page }: GetFloorsQueryParams): Observable<AjaxResponse<FloorsAPIResponse>> {
    return ajax.get(
      `${this.baseUrl}/api/space-manager/floors?${stringify({
        building_uuid: buildingUuid,
        sorting: floors_sorting,
        page,
      })}`,
      this.DefaultHeaders,
    );
  }

  getSpacesByFloor({
    floor_uuid,
    vertical_names,
    vertical_adapter_uuid,
    no_pagination,
  }: FilterSpacesQueryParams): Observable<AjaxResponse<SpaceAPIResponse>> {
    const params = stringify({ floor_uuid, vertical_names, vertical_adapter_uuid, no_pagination });
    return ajax.get(`${this.baseUrl}/api/space-manager/spaces?${params}`, this.DefaultHeaders);
  }

  fetchUiMetadata({ ownerType, ownerUuid }: OwnerParams): Observable<AjaxResponse<UiMetadata>> {
    return ajax.get(
      `${this.baseUrl}/api/verticals/${RESOURCE_BOOKING}/${ownerType.toUpperCase()}/${ownerUuid}/ui-metadata`,
      this.DefaultHeaders,
    );
  }

  getCart(cartId: string): Observable<AjaxResponse<GetCartApiResponse>> {
    return ajax.get(`${this.baseUrl}/api/payments/v1/carts/${cartId}`, this.DefaultHeaders);
  }

  submitCart(submitCartDto: SubmitOrderDto, lang?: string | null): Observable<AjaxResponse<SubmitCartApiResponse>> {
    const baseParams = lang ? { ...this.DefaultParams, lang } : this.DefaultParams;
    const params = stringify(
      submitCartDto.locationId ? { ...baseParams, locationId: submitCartDto.locationId } : { ...baseParams },
    );

    return ajax.post(
      `${this.baseUrl}/api/payments/v1/carts/${submitCartDto.cart_id}?${params}`,
      { ...submitCartDto, cart_id: undefined, locationId: undefined },
      this.DefaultHeaders,
    );
  }

  /*
   * ⚠️ `lang`, like every other read. The LIST beside it has always sent it (DefaultParams, via
   * getTransactions); this one did not, so the same booking could be asked for twice and answered
   * in two different languages depending on which route reached it.
   *
   * Whether helix acts on it for this endpoint is its business -- an unread query param costs
   * nothing, and the two calls no longer disagree about what they asked for. Worth knowing that it
   * may change nothing on its own: the list sends `lang` and still answers with the name as it was
   * SNAPSHOTTED at booking time, so translated booking names are a helix question either way.
   */
  getTransaction(transactionUuid: string): Observable<AjaxResponse<GetTransactionResponse>> {
    return ajax.get(
      `${this.baseUrl}/api/tenant/v1/users/current/transactions/${transactionUuid}?${stringify(
        this.DefaultParams,
        this.DefaultParamsOptions,
      )}`,
      this.DefaultHeaders,
    );
  }

  getRedirectTransaction(transactionUuid: string): Observable<AjaxResponse<RedirectTransactionResponse>> {
    return ajax.get(`${this.baseUrl}/api/payments/transaction/${transactionUuid}`, this.DefaultHeaders);
  }

  complete3DSCart(payload: CompleteCartDto): Observable<AjaxResponse<Complete3DSCartApiResponse>> {
    return ajax.post(`${this.baseUrl}/api/payments/complete-3ds`, payload, this.DefaultHeaders);
  }

  getPaymentMethods(cartId: string): Observable<AjaxResponse<Array<SavedPaymentMethod>>> {
    const params = stringify({ cartId, locationId: this.locationId });

    return ajax.get(`${this.baseUrl}/api/payment-methods?${params}`, this.DefaultHeaders);
  }

  savePaymentMethod(
    savePaymentMethodRequest: SavePaymentMethodRequest,
  ): Observable<AjaxResponse<SavePaymentMethodResponse>> {
    return ajax.post(`${this.baseUrl}/api/payment-methods`, savePaymentMethodRequest, this.DefaultHeaders);
  }

  deletePaymentMethod(paymentMethodId: number): Observable<AjaxResponse<[undefined, undefined]>> {
    return ajax.delete(`${this.baseUrl}/api/payment-methods/${paymentMethodId}`, this.DefaultHeaders);
  }

  updatePaymentMethod(
    updatePaymentMethodRequest: UpdatePaymentMethodRequest,
  ): Observable<AjaxResponse<SavePaymentMethodResponse>> {
    return ajax.patch(
      `${this.baseUrl}/api/payment-methods/${updatePaymentMethodRequest.paymentMethodId}`,
      { default: updatePaymentMethodRequest.default },
      this.DefaultHeaders,
    );
  }
}
